import { Controller } from "@hotwired/stimulus";

/**
 * What the basket costs, republished every time the
 * basket changes (CAL-499).
 *
 * It lives on `BasketFigureComponent`, a frame carrying
 * nothing but this number, and that is the only reason
 * it works: `ManualOrders::LinesController` replaces
 * that frame on EVERY line mutation, so this controller
 * gets a fresh `connect()` with the new figure and needs
 * no signal of its own.
 *
 * A frame of its own rather than the totals frame,
 * which is where it started. The totals frames are
 * replaced only when the request brought the right
 * column with it, because the pills and the modifier
 * steppers post a form carrying neither the discount
 * nor the order type. So mounted there this went stale
 * with them, and it is the figure every other number is
 * computed FROM.
 *
 * It does two things with it, and neither is
 * arithmetic.
 *
 * ONE: it copies the three cart figures onto
 * `manual-order-totals`, which reads them off the page
 * ROOT. No stream replaces the root, because it holds
 * the customer, fulfillment and payment cards and every
 * unsubmitted field in them. Without this the rendered
 * rows are correct after a tap while the controller that
 * recomputes them still holds the previous basket, and
 * the next thing that moves the total (a quote landing,
 * a discount typed) writes a figure an item out of date.
 * Rows right, then quietly wrong.
 *
 * TWO: if the amount actually MOVED, it asks for a new
 * courier quote.
 *
 * KEYED ON THE AMOUNT, NOT ON THE EVENT, and that is
 * what keeps it honest AND cheap. A quote is a paid call
 * to the provider, so three taps must not buy three
 * quotes: an add followed by a remove returns to the
 * same figure and buys nothing at all, and three taps
 * that genuinely move the total are three distinct
 * amounts of which the last one stands. No debounce and
 * no timer, because there is nothing to wait for: the
 * tap has already been written to the basket by the time
 * this frame arrives.
 *
 * WHY IT IS A MONEY BUG AND NOT A COSMETIC ONE: the
 * quote carries `cash_to_collect_cents`. A merchant who
 * quotes a trip, adds another bottle and sends the
 * courier on the stale quote has one collecting the
 * PRE-TAP amount. The customer hands over the smaller
 * number and the merchant is out the difference on food
 * they have already cooked. It is the exact mirror of
 * R26, which had a courier collecting money the merchant
 * already had.
 *
 * The previous figure is read back off the ROOT rather
 * than held here, because this controller does not
 * survive the replace that tells it the news: the
 * element it is mounted on IS what got replaced. The
 * root is the thing that persists, which makes it both
 * the place the old number lives and the place the new
 * one has to land.
 */
export default class extends Controller {
  static outlets = [
    "manual-order-totals",
    "courier-quote",
  ];
  static values = {
    subtotalCents: Number,
    promotionsCents: Number,
    goodsCents: Number,
  };

  connect() {
    if (!this.hasManualOrderTotalsOutlet) return;

    const root =
      this.manualOrderTotalsOutletElement.dataset;
    const moved =
      root.manualOrderTotalsGoodsCentsValue !==
      String(this.goodsCentsValue);

    // Written even when it has not moved, so the two can
    // never drift apart on a render nobody was watching.
    root.manualOrderTotalsSubtotalCentsValue =
      this.subtotalCentsValue;
    root.manualOrderTotalsPromotionsCentsValue =
      this.promotionsCentsValue;
    root.manualOrderTotalsGoodsCentsValue =
      this.goodsCentsValue;

    if (moved) this.requote();
  }

  /**
   * Only where there is a courier to re-price. A flat
   * fee does not depend on the basket, and the other
   * three fulfillments have no quote at all, so the
   * outlet is simply absent and nothing is asked for.
   */
  requote() {
    if (!this.hasCourierQuoteOutlet) return;

    this.courierQuoteOutlet.retryQuote();
  }
}
